import { apiClient } from '@/lib/api-client';
import type {
  Paginated,
  PaginationInput,
  Product,
  CreateProductInput,
  UpdateProductStatusInput,
  UpdateProductExternalKeyInput,
  ProductExternalKeyInput,
  ProductPerk,
  ApplyProductPerkBatchInput,
  ProductPerkBatchResult,
  PerkGroup,
  PerkHolderCountGroup,
  PerkHolderCount,
  ResolvedPlatformProfile,
  GrantPolicySpec,
  PerkPreset,
  PerkPresetItem,
  CreatePerkPresetInput,
  ApplyPerkPresetItemBatchInput,
  PerkPresetItemBatchResult,
  CatalogRole,
  Feature,
  UpdateScopesInput,
} from '../../domain/entities/catalog.entity';

/**
 * Uma classe, um método por endpoint. Sem lógica de negócio — só HTTP.
 * Endpoints do CatalogModule do monolith (`/admin/products` e `/admin/catalog`).
 */
export class CatalogApiDatasource {
  // ---- Products ----------------------------------------------------------

  async listProducts(input?: PaginationInput): Promise<Paginated<Product>> {
    const { data } = await apiClient.get<Paginated<Product>>(
      '/admin/products',
      {
        params: {
          page: input?.page ?? 1,
          perPage: input?.perPage ?? 10,
        },
      },
    );
    return data;
  }

  async createProduct(input: CreateProductInput): Promise<Product> {
    const { data } = await apiClient.post<Product>('/admin/products', input);
    return data;
  }

  async updateProductStatus(
    productId: string,
    input: UpdateProductStatusInput,
  ): Promise<Product> {
    const { data } = await apiClient.patch<Product>(
      `/admin/products/${productId}/status`,
      input,
    );
    return data;
  }

  async updateProductExternalKey(
    productId: string,
    input: UpdateProductExternalKeyInput,
  ): Promise<Product> {
    const { data } = await apiClient.patch<Product>(
      `/admin/products/${productId}/external-key`,
      input,
    );
    return data;
  }

  /**
   * O entregável casado pela chave externa — bloco "Entregável no Catalog" da
   * tela da oferta. Chave sem produto cadastrado devolve `null`, não 404 — e o
   * Nest responde `null` com corpo vazio, que o axios entrega como `''`; o
   * `|| null` devolve ao chamador o `null` que o tipo promete.
   */
  async findProductByExternalKey(
    key: ProductExternalKeyInput,
  ): Promise<Product | null> {
    const { data } = await apiClient.get<Product | ''>(
      '/admin/products/by-external-key',
      { params: key },
    );
    return data || null;
  }

  async listProductPerks(productId: string): Promise<ProductPerk[]> {
    const { data } = await apiClient.get<ProductPerk[]>(
      `/admin/products/${productId}/perks`,
    );
    return data;
  }

  /**
   * O diff inteiro das associações do produto, em uma escrita só. É por aqui que
   * o editor de associação grava — não há mais rota de item único no front.
   */
  async applyProductPerkBatch(
    productId: string,
    input: ApplyProductPerkBatchInput,
  ): Promise<ProductPerkBatchResult> {
    const { data } = await apiClient.post<ProductPerkBatchResult>(
      `/admin/products/${productId}/perks/batch`,
      input,
    );
    return data;
  }

  // ---- Perks -------------------------------------------------------------

  /** Uma linha por capacidade `(type, key)` — a página conta capacidades. */
  async listPerkGroups(input?: PaginationInput): Promise<Paginated<PerkGroup>> {
    const { data } = await apiClient.get<Paginated<PerkGroup>>(
      '/admin/catalog/perks',
      {
        params: {
          page: input?.page ?? 1,
          perPage: input?.perPage ?? 10,
        },
      },
    );
    return data;
  }

  /**
   * Todas as páginas de uma vez, para as telas que precisam do catálogo inteiro
   * em memória (conceder perk, associar perk a produto) — o endpoint só pagina.
   */
  async listAllPerkGroups(): Promise<PerkGroup[]> {
    const perPage = 100;
    const all: PerkGroup[] = [];
    let page = 1;
    for (;;) {
      const { items, total } = await this.listPerkGroups({ page, perPage });
      all.push(...items);
      if (items.length === 0 || all.length >= total) break;
      page += 1;
    }
    return all;
  }

  async listGrantPolicySpecs(): Promise<GrantPolicySpec[]> {
    const { data } = await apiClient.get<GrantPolicySpec[]>(
      '/admin/catalog/perks/grant-policy-kinds',
    );
    return data;
  }

  // ---- Portadores de perk (contagem + listagem) --------------------------
  //
  // Estes três endpoints servem só a tela `Catálogo → Perks`, então moram no
  // datasource dela — mesmo os dois primeiros sendo do delivery (`/admin/perks`)
  // e o terceiro do identity (`/admin/identity/...`). É a orquestração no front
  // decidida no plano: o catalog não chama delivery/identity no back.

  /** Contagem de portadores por linha (`POST` porque a lista de grupos vai no corpo). */
  async countPerkHolders(
    groups: PerkHolderCountGroup[],
  ): Promise<PerkHolderCount[]> {
    const { data } = await apiClient.post<{ counts: PerkHolderCount[] }>(
      '/admin/perks/counts',
      { groups },
    );
    return data.counts;
  }

  /** Ids de portadores de uma linha, paginado (só ids — exibição vem do resolve). */
  async listPerkHolderProfiles(
    perkIds: string[],
    input?: PaginationInput,
  ): Promise<Paginated<{ platformProfileId: string }>> {
    const { data } = await apiClient.get<
      Paginated<{ platformProfileId: string }>
    >('/admin/perks/profiles', {
      params: {
        perkIds: perkIds.join(','),
        page: input?.page ?? 1,
        perPage: input?.perPage ?? 10,
      },
    });
    return data;
  }

  /** Hidrata `platformProfileId → { name, email, scope }` (identity, dono da exibição). */
  async resolvePlatformProfiles(
    platformProfileIds: string[],
  ): Promise<ResolvedPlatformProfile[]> {
    const { data } = await apiClient.post<{
      profiles: ResolvedPlatformProfile[];
    }>('/admin/identity/platform-profiles/resolve', { platformProfileIds });
    return data.profiles;
  }

  // ---- Perk presets ------------------------------------------------------

  async listPerkPresets(
    input?: PaginationInput,
  ): Promise<Paginated<PerkPreset>> {
    const { data } = await apiClient.get<Paginated<PerkPreset>>(
      '/admin/perk-presets',
      {
        params: {
          page: input?.page ?? 1,
          perPage: input?.perPage ?? 10,
        },
      },
    );
    return data;
  }

  /** Todos os presets, para o seletor da tela de conceder. */
  async listAllPerkPresets(): Promise<PerkPreset[]> {
    const perPage = 100;
    const all: PerkPreset[] = [];
    let page = 1;
    for (;;) {
      const { items, total } = await this.listPerkPresets({ page, perPage });
      all.push(...items);
      if (items.length === 0 || all.length >= total) break;
      page += 1;
    }
    return all;
  }

  async createPerkPreset(input: CreatePerkPresetInput): Promise<PerkPreset> {
    const { data } = await apiClient.post<PerkPreset>(
      '/admin/perk-presets',
      input,
    );
    return data;
  }

  async updatePerkPreset(
    presetId: string,
    input: CreatePerkPresetInput,
  ): Promise<PerkPreset> {
    const { data } = await apiClient.patch<PerkPreset>(
      `/admin/perk-presets/${presetId}`,
      input,
    );
    return data;
  }

  /**
   * Hard delete, junto com os itens. É seguro porque o uso do preset é clonagem:
   * nada concedido depende dele para continuar valendo.
   */
  async deletePerkPreset(presetId: string): Promise<PerkPreset> {
    const { data } = await apiClient.delete<PerkPreset>(
      `/admin/perk-presets/${presetId}`,
    );
    return data;
  }

  async listPerkPresetItems(presetId: string): Promise<PerkPresetItem[]> {
    const { data } = await apiClient.get<PerkPresetItem[]>(
      `/admin/perk-presets/${presetId}/items`,
    );
    return data;
  }

  /** O diff inteiro dos itens do preset — mesma rota de lote dos outros alvos. */
  async applyPerkPresetItemBatch(
    presetId: string,
    input: ApplyPerkPresetItemBatchInput,
  ): Promise<PerkPresetItemBatchResult> {
    const { data } = await apiClient.post<PerkPresetItemBatchResult>(
      `/admin/perk-presets/${presetId}/items/batch`,
      input,
    );
    return data;
  }

  // ---- Roles -------------------------------------------------------------

  async listRoles(
    input?: PaginationInput & { includeDeprecated?: boolean },
  ): Promise<Paginated<CatalogRole>> {
    const { data } = await apiClient.get<Paginated<CatalogRole>>(
      '/admin/catalog/roles',
      {
        params: {
          page: input?.page ?? 1,
          perPage: input?.perPage ?? 10,
          ...(input?.includeDeprecated ? { includeDeprecated: true } : {}),
        },
      },
    );
    return data;
  }

  async updateRoleScopes(
    roleId: string,
    input: UpdateScopesInput,
  ): Promise<CatalogRole> {
    const { data } = await apiClient.put<CatalogRole>(
      `/admin/catalog/roles/${roleId}/scopes`,
      input,
    );
    return data;
  }

  // ---- Features ----------------------------------------------------------

  async listFeatures(
    input?: PaginationInput & { includeDeprecated?: boolean },
  ): Promise<Paginated<Feature>> {
    const { data } = await apiClient.get<Paginated<Feature>>(
      '/admin/catalog/features',
      {
        params: {
          page: input?.page ?? 1,
          perPage: input?.perPage ?? 10,
          ...(input?.includeDeprecated ? { includeDeprecated: true } : {}),
        },
      },
    );
    return data;
  }

  async updateFeatureScopes(
    featureId: string,
    input: UpdateScopesInput,
  ): Promise<Feature> {
    const { data } = await apiClient.put<Feature>(
      `/admin/catalog/features/${featureId}/scopes`,
      input,
    );
    return data;
  }
}
